import { call, put, takeLatest, delay, select } from "redux-saga/effects";
import type { PayloadAction } from "@reduxjs/toolkit";
import {
  sendOTPRequest,
  sendOTPSuccess,
  sendOTPFailure,
  verifyOTPRequest,
  verifyOTPSuccess,
  verifyOTPFailure,
  resendOTPRequest,
  checkAuthStatusRequest,
  checkAuthStatusSuccess,
  checkAuthStatusFailure,
  createStoreRequest,
  createStoreSuccess,
  createStoreFailure,
  authorizeStoreRequest,
  authorizeStoreSuccess,
  authorizeStoreFailure,
  setCurrentStore,
} from "@/store/slices/auth.slice";
import { setStoreContext } from "@/store/slices/tenant.slice";
import * as authService from "@/services/authService";
import type { RootState } from "@/store/store";
import type { AccessRole, AuthStatusTrigger, UserRole } from "@/types/auth";
import { whitelabelConfig } from "@/config/whitelabel";

const getAuthState = (state: RootState) => state.auth;
const configuredStoreDashboardPath = `/${whitelabelConfig.storeType}/${whitelabelConfig.storeId}/dashboard`;

const getAdminAuthFromStatus = (status: authService.StatusResponse) =>
  status.userId
    ? {
        userId: status.userId,
        phone: status.phoneNumber || "",
      }
    : undefined;

function getConfiguredStore(
  ...storeGroups: Array<AccessRole[] | undefined>
): AccessRole | undefined {
  return storeGroups
    .flatMap((stores) => stores || [])
    .find(
      (store) =>
        store.storeId === whitelabelConfig.storeId &&
        store.storeType === whitelabelConfig.storeType,
    );
}

function* getAuthStatusWithRetry(): Generator<unknown, authService.StatusResponse, unknown> {
  let response = (yield call(
    authService.getAuthStatus,
  )) as authService.StatusResponse;

  if (!response.valid) {
    yield delay(400);
    response = (yield call(
      authService.getAuthStatus,
    )) as authService.StatusResponse;
  }

  return response;
}

function* loginToConfiguredStore(
  initialStatus: authService.StatusResponse,
  trigger: AuthStatusTrigger | null | undefined,
): Generator<unknown, boolean, unknown> {
  const loginResponse = (yield call(
    authService.loginToStore,
    whitelabelConfig.storeId,
  )) as authService.LoginToStoreResponse;

  if (!loginResponse.valid) {
    yield put(
      checkAuthStatusFailure({
        error: loginResponse.message || "Failed to login to store",
        trigger,
        attemptCount: 2,
      }),
    );
    return false;
  }

  const statusResponse = (yield* getAuthStatusWithRetry()) as authService.StatusResponse;

  if (!statusResponse.valid) {
    yield put(
      checkAuthStatusFailure({
        error: statusResponse.message || "Failed to fetch store status",
        trigger,
        attemptCount: 2,
      }),
    );
    return false;
  }

  const selectedStore = getConfiguredStore(
    statusResponse.stores,
    loginResponse.stores,
    initialStatus.stores,
  );

  if (!selectedStore) {
    yield put(
      checkAuthStatusFailure({
        error: "Configured store was not returned by the API",
        trigger,
        attemptCount: 2,
      }),
    );
    return false;
  }

  const authorizedStore: AccessRole = {
    ...selectedStore,
    authorized: true,
  };

  const verifiedStores = statusResponse.stores.some(
    (store) =>
      store.storeId === authorizedStore.storeId &&
      store.storeType === authorizedStore.storeType,
  )
    ? statusResponse.stores.map((store) =>
        store.storeId === authorizedStore.storeId &&
        store.storeType === authorizedStore.storeType
          ? { ...store, authorized: true }
          : store,
      )
    : [...statusResponse.stores, authorizedStore];

  yield put(setCurrentStore(authorizedStore));
  yield put(
    setStoreContext({
      storeId: authorizedStore.storeId,
      storeType: authorizedStore.storeType,
    }),
  );
  yield put(authorizeStoreSuccess({ storeId: authorizedStore.storeId }));
  yield put(
    checkAuthStatusSuccess({
      isAuthorized: true,
      adminAuth: getAdminAuthFromStatus(statusResponse),
      verifiedStores,
      trigger: trigger || "post-authorize",
      pendingRoute: configuredStoreDashboardPath,
    }),
  );

  return true;
}

const derivePendingRoute = (
  trigger: AuthStatusTrigger | null | undefined,
  stores: AccessRole[],
  currentStore: AccessRole | null,
) => {
  switch (trigger) {
    case "post-verify":
      return null;
    case "post-create-store":
      return configuredStoreDashboardPath;
    case "post-authorize":
      if (currentStore) {
        return `/${currentStore.storeType}/${currentStore.storeId}/dashboard`;
      }
      return configuredStoreDashboardPath;
    case "route-guard":
      return null;
    case "manual-retry":
      return stores.length > 0 ? configuredStoreDashboardPath : "/auth/new-store";
    case "new-store-load":
    default:
      return null;
  }
};

function* sendOTPSaga(
  action: PayloadAction<{
    phoneNumber: string;
    inviteId?: string;
    channel?: string;
  }>,
): Generator<unknown, void, unknown> {
  try {
    const { phoneNumber, inviteId, channel } = action.payload;
    const cleanPhone = phoneNumber.replace(/\s/g, "");

    if (cleanPhone.length !== 10 || !/^\d{10}$/.test(cleanPhone)) {
      throw new Error("Please enter a valid 10-digit phone number");
    }

    const formattedPhone = `91${cleanPhone}`;
    const response = (yield call(
      authService.sendOTP,
      formattedPhone,
      inviteId,
      channel,
    )) as authService.SendOTPResponse;

    if (!response.valid) {
      yield put(sendOTPFailure(response.message || "Failed to send OTP"));
      return;
    }

    yield put(sendOTPSuccess({ authenticationId: response.authenticationId }));
  } catch (error: unknown) {
    yield put(
      sendOTPFailure(
        error instanceof Error ? error.message : "Failed to send OTP",
      ),
    );
  }
}

function* verifyOTPSaga(
  action: PayloadAction<{ otp: string }>,
): Generator<unknown, void, unknown> {
  try {
    const { otp } = action.payload;
    const authState = (yield select(getAuthState)) as ReturnType<
      typeof getAuthState
    >;

    if (!authState.authenticationId) {
      throw new Error("Authentication ID not found. Please request OTP again.");
    }

    const response = (yield call(
      authService.verifyOTP,
      authState.authenticationId,
      otp,
    )) as authService.VerifyOTPResponse;

    if (!response.valid) {
      yield put(verifyOTPFailure(response.message || "Invalid OTP"));
      return;
    }

    const roles: UserRole[] = response.stores.map((store) => ({
      role: store.role,
      storeId: store.storeId,
      storeType: store.storeType,
      onboardingCompleted: store.onboardingCompleted,
    }));

    yield put(
      verifyOTPSuccess({
        userId: response.userId,
        roles,
        newUser: response.newUser,
      }),
    );
    yield put(checkAuthStatusRequest({ trigger: "post-verify" }));
  } catch (error: unknown) {
    yield put(
      verifyOTPFailure(error instanceof Error ? error.message : "Invalid OTP"),
    );
  }
}

function* resendOTPSaga(): Generator<unknown, void, unknown> {
  try {
    const authState = (yield select(getAuthState)) as ReturnType<
      typeof getAuthState
    >;
    const { phoneNumber, channel } = authState;

    if (!phoneNumber) {
      throw new Error("Phone number not found. Please start over.");
    }

    yield delay(1000);

    const cleanPhone = phoneNumber.replace(/\s/g, "");
    const formattedPhone = `91${cleanPhone}`;
    const response = (yield call(
      authService.sendOTP,
      formattedPhone,
      undefined,
      channel || "whatsapp",
    )) as authService.SendOTPResponse;

    if (!response.valid) {
      yield put(sendOTPFailure(response.message || "Failed to resend OTP"));
      return;
    }

    yield put(sendOTPSuccess({ authenticationId: response.authenticationId }));
  } catch (error: unknown) {
    yield put(
      sendOTPFailure(
        error instanceof Error ? error.message : "Failed to resend OTP",
      ),
    );
  }
}

function* checkAuthStatusSaga(
  action: PayloadAction<{ trigger?: AuthStatusTrigger } | undefined>,
): Generator<unknown, void, unknown> {
  const trigger = action.payload?.trigger || null;

  try {
    const response = (yield* getAuthStatusWithRetry()) as authService.StatusResponse;

    if (!response.valid) {
      yield put(
        checkAuthStatusFailure({
          error: response.message || "Failed to check auth status",
          trigger,
          attemptCount: 2,
        }),
      );
      return;
    }

    const authState = (yield select(getAuthState)) as ReturnType<
      typeof getAuthState
    >;
    if (
      trigger === "post-verify" ||
      trigger === "manual-retry" ||
      trigger === "post-create-store"
    ) {
      const didLogin = yield* loginToConfiguredStore(response, trigger);
      if (didLogin) return;
    }

    yield put(
      checkAuthStatusSuccess({
        isAuthorized: response.valid,
        adminAuth: getAdminAuthFromStatus(response),
        verifiedStores: response.stores || [],
        trigger,
        pendingRoute: derivePendingRoute(
          trigger,
          response.stores || [],
          authState.currentStoreInfo,
        ),
      }),
    );
  } catch (error: unknown) {
    yield put(
      checkAuthStatusFailure({
        error:
          error instanceof Error
            ? error.message
            : "Failed to check auth status",
        trigger,
        attemptCount: 2,
      }),
    );
  }
}

function* createStoreSaga(
  action: PayloadAction<authService.CreateNewStoreRequest>,
): Generator<unknown, void, unknown> {
  try {
    yield call(authService.createNewStore, action.payload);
    yield put(createStoreSuccess());
    yield delay(250);
    yield put(checkAuthStatusRequest({ trigger: "post-create-store" }));
  } catch (error: unknown) {
    yield put(
      createStoreFailure(
        error instanceof Error
          ? error.message
          : "Network error occurred while creating store",
      ),
    );
  }
}

function* authorizeStoreSaga(
  action: PayloadAction<AccessRole>,
): Generator<unknown, void, unknown> {
  try {
    const store = action.payload;

    if (
      store.storeId !== whitelabelConfig.storeId ||
      store.storeType !== whitelabelConfig.storeType
    ) {
      yield put(authorizeStoreFailure("Invalid configured store access"));
      return;
    }

    const loginResponse = (yield call(
      authService.loginToStore,
      store.storeId,
    )) as authService.LoginToStoreResponse;

    if (!loginResponse.valid) {
      yield put(
        authorizeStoreFailure(
          loginResponse.message || "Failed to login to store",
        ),
      );
      return;
    }

    let statusResponse = (yield call(
      authService.getAuthStatus,
    )) as authService.StatusResponse;

    if (!statusResponse.valid) {
      yield delay(400);
      statusResponse = (yield call(
        authService.getAuthStatus,
      )) as authService.StatusResponse;
    }

    if (!statusResponse.valid) {
      yield put(
        authorizeStoreFailure(
          statusResponse.message || "Failed to fetch store status",
        ),
      );
      return;
    }

    const matchedStore =
      statusResponse.stores.find(
        (candidate) =>
          candidate.storeId === store.storeId &&
          candidate.storeType === store.storeType,
      ) ||
      loginResponse.stores.find(
        (candidate) =>
          candidate.storeId === store.storeId &&
          candidate.storeType === store.storeType,
      ) ||
      store;

    const selectedStore: AccessRole = {
      ...matchedStore,
      authorized: true,
    };

    const verifiedStores = statusResponse.stores.some(
      (candidate) =>
        candidate.storeId === selectedStore.storeId &&
        candidate.storeType === selectedStore.storeType,
    )
      ? statusResponse.stores.map((candidate) =>
          candidate.storeId === selectedStore.storeId &&
          candidate.storeType === selectedStore.storeType
            ? { ...candidate, authorized: true }
            : candidate,
        )
      : [...statusResponse.stores, selectedStore];

    yield put(setCurrentStore(selectedStore));
    yield put(
      setStoreContext({
        storeId: selectedStore.storeId,
        storeType: selectedStore.storeType,
      }),
    );
    yield put(authorizeStoreSuccess({ storeId: selectedStore.storeId }));
    yield put(
      checkAuthStatusSuccess({
        isAuthorized: true,
        adminAuth: statusResponse.userId
          ? {
              userId: statusResponse.userId,
              phone: statusResponse.phoneNumber || "",
            }
          : undefined,
        verifiedStores,
        trigger: "post-authorize",
        pendingRoute: `/${selectedStore.storeType}/${selectedStore.storeId}/dashboard`,
      }),
    );
  } catch (error: unknown) {
    yield put(
      authorizeStoreFailure(
        error instanceof Error ? error.message : "Failed to login to store",
      ),
    );
  }
}

export function* authSaga() {
  yield takeLatest(sendOTPRequest.type, sendOTPSaga);
  yield takeLatest(verifyOTPRequest.type, verifyOTPSaga);
  yield takeLatest(resendOTPRequest.type, resendOTPSaga);
  yield takeLatest(checkAuthStatusRequest.type, checkAuthStatusSaga);
  yield takeLatest(createStoreRequest.type, createStoreSaga);
  yield takeLatest(authorizeStoreRequest.type, authorizeStoreSaga);
}
